import type { AkpError, PublicConfig, RouteKey } from '@akpl/shared';
import { type ApiClient, createApiClient, isApiErrorBody } from '@akpl/shared';

import { currentLocale } from './i18n';

/** Where the API lives. Build-time default; the app config itself comes from `/v1/config`. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787').replace(
  /\/$/,
  '',
);

export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? 'dev';

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

/** Installed by the boot sequence once Firebase Auth is ready (docs/05 §1). */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/** The signed-in user's ID token, for the calls that do not go through the typed client. */
export function getAccessToken(): Promise<string | null> {
  return tokenProvider();
}

type DeviceIdProvider = () => string | null;

let deviceIdProvider: DeviceIdProvider = () => null;

/**
 * `X-Akapella-Device` (docs/07 §3). On a phone the device is **the TV being paired**, not the
 * handset: WP2.2 installs a provider that returns the paired device id for the duration of the
 * `/tv` flow so the user-upsert hook can redeem that device's pending grant.
 */
export function setDeviceIdProvider(provider: DeviceIdProvider): void {
  deviceIdProvider = provider;
}

export type ApiErrorHandler = (error: AkpError, info: { route: RouteKey }) => void;

let apiErrorHandler: ApiErrorHandler | null = null;

export function setApiErrorHandler(handler: ApiErrorHandler | null): void {
  apiErrorHandler = handler;
}

/** There is one slot, and `sessionEnd.ts` already uses it: read it to chain instead of replacing. */
export function getApiErrorHandler(): ApiErrorHandler | null {
  return apiErrorHandler;
}

const IS_TEST = import.meta.env.MODE === 'test';

/**
 * UX-404: with the phone offline a request used to burn the client's full budget — three
 * retries at a 15 s timeout each — before anything gave up, which is why the search screen sat
 * on "Cargando…" for the whole 20 s of the test and would have sat there far longer. There is
 * nothing to wait for when the device says it has no network, so the attempt fails at once and
 * the screen gets to say "Sin conexión" instead of spinning.
 */
function offlineAwareFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.reject(new TypeError('offline'));
  }
  return globalThis.fetch(input, init);
}

/** Typed client shared by every route and hook (`api.me()`, `api.sessionJoin({ body })`, …). */
export const api: ApiClient = createApiClient({
  baseUrl: API_BASE,
  getToken: () => tokenProvider(),
  getDeviceId: () => deviceIdProvider(),
  headers: () => ({ 'Accept-Language': currentLocale() }),
  // Resolve `fetch` per call (tests stub the global after this module is evaluated).
  fetch: offlineAwareFetch,
  validateResponses: import.meta.env.DEV,
  onError: (error, info) => apiErrorHandler?.(error, { route: info.route }),
  retries: IS_TEST ? 0 : 2,
  retryBaseMs: IS_TEST ? 0 : 300,
  timeoutMs: IS_TEST ? 2000 : 15_000,
});

/**
 * `GET /config` without the typed client: it runs before anything else in the boot sequence,
 * so it must not depend on i18n or on the token provider being installed.
 */
export async function fetchPublicConfig(signal?: AbortSignal): Promise<PublicConfig> {
  const res = await fetch(`${API_BASE}/v1/config`, { signal });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message = isApiErrorBody(body) ? body.error.message : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body as PublicConfig;
}
